import { useEffect, useCallback, useState } from "react";
import { MessageSquare } from "lucide-react";
import { isTauri } from "@/hooks/usePlatform";
import { useAiChatStore } from "@/stores/aiChatStore";
import { useChatTabStore } from "@/stores/chatTabStore";
import { ChatTabBar } from "./ChatTabBar";
import { ChatConversation } from "./ChatConversation";
import { ChatInput } from "./ChatInput";
import type { ChatAttachment } from "@/types/content-blocks";

interface ColonyChatTabProps {
  projectId: number;
  isLocal: boolean;
  localPath?: string;
}

function ActiveChat({ projectId, localPath }: { projectId: number; localPath: string }) {
  const activeTab = useChatTabStore((s) => s.getActiveTab(projectId));
  const createTab = useChatTabStore((s) => s.createTab);
  const linkSession = useChatTabStore((s) => s.linkSession);
  const renameTab = useChatTabStore((s) => s.renameTab);
  const tabs = useChatTabStore((s) => s.tabs[projectId] ?? []);

  const sessions = useAiChatStore((s) => s.sessions);
  const createSession = useAiChatStore((s) => s.createSession);
  const sendMessage = useAiChatStore((s) => s.sendMessage);
  const stopSession = useAiChatStore((s) => s.stopSession);
  const checkProviders = useAiChatStore((s) => s.checkProviders);

  const [sendError, setSendError] = useState<string | null>(null);

  const [hydrated, setHydrated] = useState(useChatTabStore.persist.hasHydrated());
  useEffect(() => {
    if (hydrated) return;
    const unsub = useChatTabStore.persist.onFinishHydration(() => setHydrated(true));
    return unsub;
  }, [hydrated]);

  useEffect(() => { checkProviders(); }, [checkProviders]);

  useEffect(() => {
    if (!hydrated) return;
    if (tabs.length === 0) {
      createTab(projectId, "claude-code");
    }
  }, [hydrated, projectId, tabs.length, createTab]);

  const session = activeTab?.sessionId ? sessions.get(activeTab.sessionId) ?? null : null;
  const isStreaming = session?.state === "running";
  const provider = activeTab?.provider ?? "claude-code";

  const handleSend = useCallback(async (message: string, _attachments: ChatAttachment[]) => {
    if (!activeTab) return;
    setSendError(null);

    try {
      if (activeTab.sessionId && session) {
        await sendMessage(activeTab.sessionId, message);
      } else {
        const sessionId = await createSession(activeTab.provider, localPath, message);
        linkSession(activeTab.id, sessionId);
        const name = message.length > 40 ? message.slice(0, 37) + "..." : message;
        renameTab(activeTab.id, name);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[ColonyChatTab] handleSend failed:", err);
      setSendError(msg);
    }
  }, [activeTab, session, localPath, createSession, sendMessage, linkSession, renameTab]);

  const handleStop = useCallback(() => {
    if (activeTab?.sessionId) {
      stopSession(activeTab.sessionId);
    }
  }, [activeTab, stopSession]);

  const handleQuickAction = useCallback((prompt: string) => {
    handleSend(prompt, []);
  }, [handleSend]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ChatTabBar projectId={projectId} />
      <ChatConversation session={session} provider={provider} isSessionRunning={isStreaming} onQuickAction={handleQuickAction} />
      {sendError && (
        <div className="mx-4 mb-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20 text-[12px] text-red-400">
          <span className="font-medium">Error:</span> {sendError}
        </div>
      )}
      <ChatInput
        onSend={handleSend}
        onStop={handleStop}
        isStreaming={isStreaming}
        disabled={!activeTab}
      />
    </div>
  );
}

export function ColonyChatTab({ projectId, isLocal, localPath }: ColonyChatTabProps) {
  if (!isTauri()) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-0 px-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-10 h-10 rounded-[10px] bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
            <MessageSquare size={20} className="text-zinc-500" />
          </div>
          <p className="text-[13px] text-zinc-400">Chat requires the desktop app</p>
          <p className="text-[11px] text-zinc-500 max-w-[280px]">
            AI chat is available when running Colony as a desktop application with local CLI tools installed.
          </p>
        </div>
      </div>
    );
  }

  if (!localPath) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-0 px-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-10 h-10 rounded-[10px] bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
            <MessageSquare size={20} className="text-zinc-500" />
          </div>
          <p className="text-[13px] text-zinc-400">No local path configured</p>
          <p className="text-[11px] text-zinc-500 max-w-[280px]">Set a local project path to enable AI chat.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-[#0a0a0c]">
      <ActiveChat projectId={projectId} localPath={localPath} />
    </div>
  );
}
